import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import SuperAdminLayout from "./SuperAdminLayout";
import { fetchStats, fetchPlatformStats, resetDemo } from "@/services/superadminApi";
import {
    SectionHeader,
    StatCard,
    CardGlass,
    Badge,
    Button
} from "@/components/ui/PremiumComponents";
import {
    Store,
    CreditCard,
    TrendingUp,
    ShoppingBag,
    Zap,
    Map as MapIcon,
    ShieldAlert,
    RefreshCcw,
    Users,
    Ticket,
    Truck
} from 'lucide-react';

export default function SuperAdminDashboard() {
    const [stats, setStats] = useState(null);
    const [platformStats, setPlatformStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [resetting, setResetting] = useState(false);

    useEffect(() => {
        Promise.all([fetchStats(), fetchPlatformStats()])
            .then(([s, p]) => {
                setStats(s);
                setPlatformStats(p);
            })
            .catch(() => setError("Error de conexión con el núcleo Conexo OS."))
            .finally(() => setLoading(false));
    }, []);

    const byPlan = stats?.subscriptions?.by_plan || {};

    const handleReset = async () => {
        if (!window.confirm("¿Confirmas el reset completo de los datos de simulación?")) return;
        setResetting(true);
        try {
            const res = await resetDemo();
            toast.success(res.message);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error en comando reset");
        } finally {
            setResetting(false);
        }
    };

    return (
        <SuperAdminLayout>
            <div className="page-fade-in">
                <SectionHeader
                    title="Panel de Control Global"
                    subtitle="Estado operativo de la infraestructura de Conexo POS en tiempo real."
                    actions={
                        <div className="flex flex-col items-end gap-2">
                            <Badge status="success" className="animate-pulse">SISTEMA OPERATIVO</Badge>
                            <span className="text-[10px] font-black uppercase tracking-tighter" style={{ color: 'var(--text-soft)' }}>
                                {stats?.generated_at ? `Sincronizado: ${new Date(stats.generated_at).toLocaleTimeString("es-ES")}` : "Sincronizando..."}
                            </span>
                        </div>
                    }
                />

                {error && <div className="bg-danger/10 border border-danger/20 p-4 rounded-xl text-danger mb-8 font-bold">⚠️ {error}</div>}

                {/* Main Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6 mb-12">
                    <StatCard
                        icon={<Store size={22} />}
                        label="Restaurantes activos"
                        value={stats?.companies?.total || 0}
                        color="#F59E0B"
                        delta={12}
                        trend="up"
                    />
                    <StatCard
                        icon={<CreditCard size={22} />}
                        label="Suscripciones"
                        value={stats?.subscriptions?.total || 0}
                        color="#10B981"
                        delta={5}
                        trend="up"
                    />
                    <StatCard
                        icon={<TrendingUp size={22} />}
                        label="MRR Mensual"
                        value={stats?.mrr_eur ? `€${stats.mrr_eur.toLocaleString("es-ES")}` : "€0"}
                        color="#8B5CF6"
                        delta={8.5}
                        trend="up"
                    />
                    <StatCard
                        icon={<ShoppingBag size={22} />}
                        label="Pedidos hoy"
                        value={stats?.orders_today || 0}
                        color="#3B82F6"
                        delta={24}
                        trend="up"
                    />
                    <StatCard
                        icon={<Zap size={22} />}
                        label="En Trial"
                        value={stats?.tenants_trial || 0}
                        color="#F472B6"
                    />
                </div>

                {/* Platform Overview Phase 11 & Integrations */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                    <CardGlass className="p-6 border-l-4 border-l-blue-500">
                        <div className="flex justify-between items-center mb-4">
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Base de Datos Global</span>
                            <Users size={16} className="text-blue-500" />
                        </div>
                        <div className="text-4xl font-black text-white tracking-tighter mb-1">
                            {platformStats?.total_customers?.toLocaleString() || 0}
                        </div>
                        <div className="text-xs font-bold text-gray-500 uppercase tracking-tighter">Clientes Únicos</div>
                    </CardGlass>
                    <CardGlass className="p-6 border-l-4 border-l-green-500">
                        <div className="flex justify-between items-center mb-4">
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Ventas Totales Red</span>
                            <TrendingUp size={16} className="text-green-500" />
                        </div>
                        <div className="text-4xl font-black text-white tracking-tighter mb-1">
                            €{platformStats?.total_sales_eur?.toLocaleString() || "0.00"}
                        </div>
                        <div className="text-xs font-bold text-gray-500 uppercase tracking-tighter">Facturación Global</div>
                    </CardGlass>
                    <CardGlass className="p-6 border-l-4 border-l-purple-500">
                        <div className="flex justify-between items-center mb-4">
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Pedidos Integrados</span>
                            <Store size={16} className="text-purple-500" />
                        </div>
                        <div className="text-4xl font-black text-white tracking-tighter mb-1">
                            {platformStats?.total_integration_orders?.toLocaleString() || 0}
                        </div>
                        <div className="text-xs font-bold text-gray-500 uppercase tracking-tighter">Uber / Glovo / Just Eat</div>
                    </CardGlass>
                    <CardGlass className="p-6 border-l-4 border-l-blue-400">
                        <div className="flex justify-between items-center mb-4">
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Delivery Hub Global</span>
                            <Truck size={16} className="text-blue-400" />
                        </div>
                        <div className="text-4xl font-black text-white tracking-tighter mb-1">
                            {platformStats?.total_hub_orders?.toLocaleString() || 0}
                        </div>
                        <div className="text-xs font-bold text-gray-500 uppercase tracking-tighter">Pedidos Despachados</div>
                    </CardGlass>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                    {/* Plans Distribution */}
                    <div className="lg:col-span-2 space-y-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-black text-white tracking-tight">Distribución por Plan</h2>
                            <Link to="/superadmin/tenants" className="text-primary text-xs font-bold hover:underline">Ver directorio completo →</Link>
                        </div>
                        <CardGlass className="p-8">
                            {Object.entries(byPlan).length > 0 ? (
                                <div className="space-y-8">
                                    {Object.entries(byPlan).map(([plan, count]) => (
                                        <div key={plan} className="space-y-3">
                                            <div className="flex justify-between items-center text-sm">
                                                <span className="text-white font-bold tracking-tight">{plan.toUpperCase()}</span>
                                                <span className="font-black uppercase text-[10px] tracking-widest" style={{ color: 'var(--text-soft)' }}>{count} clientes</span>
                                            </div>
                                            <div className="h-2.5 w-full bg-white/5 rounded-full overflow-hidden border border-white/5">
                                                <div
                                                    className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-1000 ease-out shadow-[0_0_10px_rgba(59,130,246,0.3)]"
                                                    style={{ width: `${(count / (stats?.companies?.total || 1)) * 100}%` }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="py-20 text-center font-bold" style={{ color: 'var(--text-soft)' }}>No hay datos de planes disponibles en este momento.</div>
                            )}
                        </CardGlass>
                    </div>

                    {/* Quick Control Console */}
                    <div className="space-y-6">
                        <h2 className="text-xl font-black text-white tracking-tight">Consola de Comandos</h2>
                        <div className="flex flex-col gap-4">
                            <Link to="/superadmin/map" className="group">
                                <CardGlass className="!p-4 hover:bg-white/5 transition-all">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary transform group-hover:scale-110 transition-transform">
                                            <MapIcon size={24} />
                                        </div>
                                        <div>
                                            <div className="text-sm font-bold text-white">Mapa de Operaciones</div>
                                            <div className="text-[10px] font-medium" style={{ color: 'var(--text-soft)' }}>Visualización en vivo</div>
                                        </div>
                                    </div>
                                </CardGlass>
                            </Link>

                            <Link to="/superadmin/events" className="group">
                                <CardGlass className="!p-4 hover:bg-white/5 transition-all">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-warning/10 flex items-center justify-center text-warning transform group-hover:scale-110 transition-transform">
                                            <ShieldAlert size={24} />
                                        </div>
                                        <div>
                                            <div className="text-sm font-bold text-white">Eventos de Seguridad</div>
                                            <div className="text-[10px] font-medium" style={{ color: 'var(--text-soft)' }}>Auditoría y control</div>
                                        </div>
                                    </div>
                                </CardGlass>
                            </Link>

                            <button onClick={handleReset} disabled={resetting} className="group w-full text-left outline-none">
                                <CardGlass className="!p-4 border-danger/20 hover:bg-danger/5 transition-all">
                                    <div className="flex items-center gap-4">
                                        <div className={`w-12 h-12 rounded-xl bg-danger/10 flex items-center justify-center text-danger transform group-hover:rotate-180 transition-transform duration-500`}>
                                            <RefreshCcw size={24} />
                                        </div>
                                        <div>
                                            <div className="text-sm font-bold text-danger">Purga de Datos Demo</div>
                                            <div className="text-[10px] text-danger/60 font-medium truncate">{resetting ? "Reseteando..." : "Limpiar historial de prueba"}</div>
                                        </div>
                                    </div>
                                </CardGlass>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </SuperAdminLayout>
    );
}
